import { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import portalDataService from '../../services/portalDataService';
import usePortalList from '../hooks/usePortalList';
import PortalFilterBar from '../components/PortalFilterBar';
import PortalStatusBadge from '../components/PortalStatusBadge';
import PortalSaleViewModal from '../components/PortalSaleViewModal';
import { formatDate, formatMoney } from '../portalUtils';
import { UI_COLORS } from '../../utils/colors';

// Sales Orders tab = what the customer SELLS to the company (company purchase
// orders). PO-shaped: po_number, counterparty_company ("To"), `unit` (singular).
const ALL_STATUSES = ['pending', 'confirmed', 'partial_advice_created', 'advice_created', 'loading', 'loaded', 'in_progress', 'received', 'completed', 'cancelled'];
const PAGE_SIZE = 20;

const PortalSalesPage = () => {
  const { t } = useTranslation();
  const s = t('portal.sales', { returnObjects: true });
  const c = t('portal.common', { returnObjects: true });
  const rawStatusLabels = t('portal.sales.statuses', { returnObjects: true });
  const statusLabels = (rawStatusLabels && typeof rawStatusLabels === 'object') ? rawStatusLabels : {};

  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetcher = useCallback((params) => portalDataService.getSales(params), []);
  const filters = useMemo(() => ({ status, from_date: fromDate, to_date: toDate }), [status, fromDate, toDate]);
  const { items, total, totalPages, loading, error, reload } = usePortalList(fetcher, filters, page, PAGE_SIZE);

  // /portal/sales-orders may not be deployed yet (404). Treat 404 as "no data"
  // so the tab shows an empty state instead of an error.
  const notDeployed = error && /not found|404/i.test(String(error));

  const rows = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((o) =>
      (o.po_number || '').toLowerCase().includes(q) ||
      (o.counterparty_company || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const statusOptions = useMemo(
    () => [{ value: '', label: c?.allStatuses || 'All statuses' }, ...ALL_STATUSES.map((v) => ({ value: v, label: statusLabels[v] || v }))],
    [c, statusLabels]
  );

  const detailReqRef = useRef(0);
  const openDetail = useCallback(async (row) => {
    const reqId = ++detailReqRef.current;
    setSelected(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await portalDataService.getSale(row.id);
      if (reqId !== detailReqRef.current) return;
      // On a failed/404 detail fetch show the modal's empty state, not the stale
      // list row dressed up as the real detail.
      setDetail(res.success ? res.data : null);
    } catch (err) {
      console.error('Failed to load sales order detail:', err);
      if (reqId === detailReqRef.current) setDetail(null);  // show empty state, not the stale list row
    } finally {
      // A superseded request must not clear the flag the newer one owns.
      if (reqId === detailReqRef.current) setDetailLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    { id: 'po_number', label: s?.columns?.poNumber || 'Order No.', renderCell: (o) => <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{o.po_number || '—'}</span> },
    { id: 'date', label: s?.columns?.date || 'Date', renderCell: (o) => formatDate(o.date) },
    { id: 'counterparty', label: s?.columns?.counterparty || 'To', renderCell: (o) => o.counterparty_company || '—' },
    { id: 'total_amount', label: s?.columns?.total || 'Total', align: 'right', renderCell: (o) => formatMoney(o.total_amount) },
    { id: 'status', label: s?.columns?.status || 'Status', renderCell: (o) => <PortalStatusBadge status={o.status} labels={statusLabels} /> },
    { id: 'actions', label: c?.actions || 'Actions', align: 'center', renderCell: (o) => (
      <button type="button" onClick={() => openDetail(o)} className="px-3 py-1 rounded-lg text-xs font-semibold" style={{ color: UI_COLORS.BRAND.TERTIARY, backgroundColor: 'rgba(24,154,180,0.1)' }}>
        {c?.view || 'View'}
      </button>
    ) },
  ], [s, c, statusLabels, openDetail]);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'Sales Orders'}</h1>

      <PortalFilterBar
        statusOptions={statusOptions}
        status={status}
        onStatusChange={(v) => { setStatus(v); setPage(1); }}
        showDates
        fromDate={fromDate} toDate={toDate}
        onFromDateChange={(v) => { setFromDate(v); setPage(1); }}
        onToDateChange={(v) => { setToDate(v); setPage(1); }}
        search={search} onSearchChange={setSearch}
      />

      {error && !notDeployed && !loading && (
        // 404 is treated as "not deployed yet" → empty state (above). But a real
        // error (500/network) used to also render as "No sales orders"; show it.
        <div role="alert" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>{typeof error === 'string' ? error : 'Could not load your orders. Please try again.'}</span>
          <button onClick={() => reload()} style={{ fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>Retry</button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={notDeployed ? [] : rows}
        loading={loading}
        keyField="id"
        enablePagination={false}
        emptyState={{ title: s?.empty?.title || 'No sales orders', message: s?.empty?.message || 'Orders you supply will appear here.' }}
      />

      {!search.trim() && !notDeployed && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={total}
          onPageChange={setPage}
          loading={loading}
          totalLabel={s?.totalLabel || 'total sales orders'}
          pageSize={PAGE_SIZE}
        />
      )}

      <PortalSaleViewModal isOpen={!!selected} onClose={() => setSelected(null)} order={detail} loading={detailLoading} statusLabels={statusLabels} />
    </div>
  );
};

export default PortalSalesPage;
